// Centralized event schema data for consistent JSON-LD across all pages

export const EVENT_LOCATION = {
  "@type": "Place" as const,
  "name": "The Roostertail",
  "address": {
    "@type": "PostalAddress" as const,
    "streetAddress": "100 Marquette Drive",
    "addressLocality": "Detroit",
    "addressRegion": "MI",
    "postalCode": "48214",
    "addressCountry": "US",
  },
  "geo": {
    "@type": "GeoCoordinates" as const,
    "latitude": 42.3369,
    "longitude": -82.9961,
  },
  "url": "https://www.roostertail.com",
  "sameAs": "https://www.google.com/maps/place/The+Roostertail/@42.3369,-82.9961",
};

export const EVENT_ORGANIZER = {
  "@type": "Organization" as const,
  "name": "Yacht Rock Detroit",
  "url": "https://yachtrockdetroit.com",
  "logo": "https://yachtrockdetroit.com/favicon.svg",
  "sameAs": [
    "https://www.instagram.com/yachtrockdetroit/",
    "https://www.facebook.com/yachtrockdetroit/",
  ],
};

export const EVENT_PERFORMERS = [
  {
    "@type": "MusicGroup" as const,
    "name": "Yacht Rock Revue",
    "description": "Atlanta-based yacht rock tribute band performing Hall & Oates, Steely Dan, and classic yacht rock hits",
    "url": "https://www.yachtrockrevue.com",
    "sameAs": [
      "https://en.wikipedia.org/wiki/Yacht_Rock_Revue",
      "https://open.spotify.com/artist/0V1hEBOwFHEFMl00FwCYzp",
    ],
  },
  {
    "@type": "MusicGroup" as const,
    "name": "Three Dog Night",
    "description": "Legendary rock band known for 'Joy to the World,' 'One,' and 'Mama Told Me Not to Come'",
    "url": "https://www.threedognight.com",
    "sameAs": [
      "https://en.wikipedia.org/wiki/Three_Dog_Night",
      "https://open.spotify.com/artist/4FAEZeJcsYYBkNq2D3KBYA",
    ],
  },
  {
    "@type": "MusicGroup" as const,
    "name": "Pablo Cruise",
    "description": "Yacht rock legends known for 'Love Will Find a Way' and 'Whatcha Gonna Do?'",
    "sameAs": [
      "https://en.wikipedia.org/wiki/Pablo_Cruise",
      "https://open.spotify.com/artist/3xMfMDJaAWdCDMlXsYPVox",
    ],
  },
  {
    "@type": "MusicGroup" as const,
    "name": "Ambrosia",
    "description": "Smooth rock band behind 'Biggest Part of Me' and 'How Much I Feel'",
    "sameAs": [
      "https://en.wikipedia.org/wiki/Ambrosia_(band)",
      "https://open.spotify.com/artist/3MATYZ0YJQc0iB8MqzOzFk",
    ],
  },
  {
    "@type": "Person" as const,
    "name": "Elliot Lurie",
    "description": "Frontman of Looking Glass, singer and writer of 'Brandy (You're a Fine Girl)'",
    "sameAs": "https://en.wikipedia.org/wiki/Elliot_Lurie",
  },
  {
    "@type": "Person" as const,
    "name": "Peter Beckett",
    "description": "Lead vocalist of Player, voice behind the #1 hit 'Baby Come Back'",
    "sameAs": "https://en.wikipedia.org/wiki/Peter_Beckett",
  },
  {
    "@type": "Person" as const,
    "name": "Walter Egan",
    "description": "Singer-songwriter known for 'Magnet and Steel,' produced with Fleetwood Mac members",
    "sameAs": "https://en.wikipedia.org/wiki/Walter_Egan",
  },
  {
    "@type": "Person" as const,
    "name": "John Ford Coley",
    "description": "Half of England Dan & John Ford Coley, known for 'I'd Really Love to See You Tonight'",
    "sameAs": "https://en.wikipedia.org/wiki/John_Ford_Coley",
  },
  {
    "@type": "MusicGroup" as const,
    "name": "Seven Wonders",
    "description": "Premier Fleetwood Mac tribute band performing 'Go Your Own Way,' 'Dreams,' and 'The Chain'",
  },
];

export const EVENT_OFFERS = [
  {
    "@type": "Offer" as const,
    "name": "General Admission",
    "price": "219",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock",
    "validFrom": "2026-03-07T10:00:00-05:00",
    "url": "https://yachtrockdetroit.com/tickets",
    "description": "Two-day weekend pass — all 9 acts, full venue access, floating DJ deck, food & drinks for purchase",
  },
  {
    "@type": "Offer" as const,
    "name": "VIP Admiral's Pass",
    "price": "450",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock",
    "validFrom": "2026-03-07T10:00:00-05:00",
    "url": "https://yachtrockdetroit.com/tickets",
    "description": "Two-day weekend pass — all 9 acts, open bar all weekend, free food, Admirals Lounge access, priority entry",
  },
  {
    "@type": "Offer" as const,
    "name": "Single Day Pass",
    "price": "149",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock",
    "validFrom": "2026-05-24T12:00:00-04:00",
    "url": "https://yachtrockdetroit.com/tickets",
    "description": "One-day pass — Friday or Saturday — all that day's acts, floating DJ deck, full open-area access, food & drinks for purchase",
  },
];

export const EVENT_IMAGES = [
  "https://firebasestorage.googleapis.com/v0/b/yrd-2026.firebasestorage.app/o/Yacht%20Rock%20Detroit_Crowd%20Shot%20Sunset%20View%20of%20Stage%20and%20Audience.webp?alt=media",
  "https://firebasestorage.googleapis.com/v0/b/yrd-2026.firebasestorage.app/o/Yacht%20Rock%20Detroit_Stage%20Shot%20Band%20on%20Large%20Outdoor%20Stage.webp?alt=media",
  "https://firebasestorage.googleapis.com/v0/b/yrd-2026.firebasestorage.app/o/Yacht%20Rock%20Detroit_Stage%20Shot%20Full%20Band%20Performing%20in%20Captain%20Hats.webp?alt=media",
];

/**
 * Build the complete MusicEvent JSON-LD object.
 * Call this from any page that needs event schema.
 */
export function buildMusicEventSchema(overrides?: {
  url?: string;
  description?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "MusicEvent",
    "name": "Yacht Rock Detroit 2026",
    "description":
      overrides?.description ||
      "Yacht Rock Detroit is a two-day waterfront music festival at The Roostertail in Detroit, MI — July 17-18, 2026. Featuring live concerts by Three Dog Night, Pablo Cruise, Ambrosia, Yacht Rock Revue, Elliot Lurie, Peter Beckett, Walter Egan, John Ford Coley, and Seven Wonders. Detroit's smoothest summer concert festival on the Detroit River.",
    "startDate": "2026-07-17T16:00:00-04:00",
    "endDate": "2026-07-19T00:00:00-04:00",
    "doorTime": "2026-07-17T16:00:00-04:00",
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "location": EVENT_LOCATION,
    "organizer": EVENT_ORGANIZER,
    "performer": EVENT_PERFORMERS,
    "offers": EVENT_OFFERS,
    "image": EVENT_IMAGES,
    "url": overrides?.url || "https://yachtrockdetroit.com",
    "typicalAgeRange": "21+",
    "isAccessibleForFree": false,
    "inLanguage": "en",
    "maximumPhysicalAttendeeCapacity": 2000,
    "keywords":
      "yacht rock, concert Detroit, summer concerts Detroit, waterfront music festival, live music Detroit 2026, Detroit summer festival, upcoming concerts Detroit, Detroit music festival",
  };
}
